import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Info } from "lucide-react";
import { formatLKR } from "@/lib/api";

/**
 * IntrinsicValueSection — Phase 6 Stage 2
 *
 * Two side-by-side valuation cards (Graham Number / DDM Fair Value) with
 * coloured borders driven by combined price-vs-fair-value position.
 *
 * For investment-trust companies, both cards collapse into a single grey
 * "not applicable" notice.
 */

const fmtPct = (pct, withSign = false) => {
  if (pct == null || Number.isNaN(pct)) return null;
  const sign = withSign ? (pct > 0 ? "+" : pct < 0 ? "−" : "") : "";
  return `${sign}${Math.abs(pct).toFixed(1)}%`;
};

const computeDeltaPct = (price, fair) => {
  if (!price || !fair || fair <= 0 || price <= 0) return null;
  // Negative => below fair (cheaper). Positive => above fair (more expensive).
  return ((price - fair) / fair) * 100;
};

// Tone for a single card based on its own delta
//  - delta < 0 (price below fair value) = green / discount
//  - delta > 0 (price above fair value) = red / premium
//  - missing data => neutral
const cardTone = (delta) => {
  if (delta == null) return "neutral";
  if (delta < 0) return "good";
  if (delta > 0) return "bad";
  return "neutral";
};

// Combined-position tone for outer borders (per user spec):
//  Both below   → both green   (strong value)
//  Both above   → both red     (price exceeds both)
//  One above one below → amber (mixed)
//  Missing one  → use the available one's tone, fallback amber
const combinedBorder = (gd, dd) => {
  if (gd == null && dd == null) return "border-border";
  if (gd != null && dd != null) {
    if (gd < 0 && dd < 0) return "border-success/50";
    if (gd > 0 && dd > 0) return "border-destructive/50";
    return "border-warning/60";
  }
  const one = gd ?? dd;
  if (one < 0) return "border-success/50";
  if (one > 0) return "border-destructive/50";
  return "border-border";
};

const interpretGraham = (delta) => {
  if (delta == null) return "Insufficient data — EPS or Book Value not available.";
  const abs = Math.abs(delta).toFixed(0);
  if (delta < 0) {
    return `Trading at ${abs}% DISCOUNT to Graham Number — potentially undervalued by Graham's measure.`;
  }
  if (delta > 0) {
    return `Trading at ${abs}% premium to Graham Number — price exceeds Benjamin Graham's fair value estimate.`;
  }
  return "Trading exactly at Graham Number — fairly valued by Graham's measure.";
};

const interpretDdm = (delta, hasDps) => {
  if (!hasDps) {
    return "No dividend data available — DDM requires dividend per share.";
  }
  if (delta == null) return "Insufficient data — DPS or current price not available.";
  const abs = Math.abs(delta).toFixed(0);
  if (delta < 0) {
    return `Trading at ${abs}% DISCOUNT to DDM fair value — dividend stream justifies a higher price.`;
  }
  if (delta > 0) {
    return `Trading at ${abs}% premium to DDM fair value — above what the dividend stream justifies at a 12% required return.`;
  }
  return "Trading exactly at DDM fair value — dividend stream supports current price.";
};

const ValuationCard = ({
  title,
  formula,
  assumptions,
  fairValue,
  currentPrice,
  delta,
  interpretation,
  testId,
}) => {
  const tone = cardTone(delta);
  const border =
    tone === "good" ? "border-success/50"
    : tone === "bad" ? "border-destructive/50"
    : "border-border";
  const diffColor =
    tone === "good" ? "text-success"
    : tone === "bad" ? "text-destructive"
    : "text-muted-foreground";

  return (
    <Card className={`border-2 ${border} bg-card transition-colors`} data-testid={testId}>
      <CardHeader className="pb-2">
        <CardTitle className="font-display text-base">{title}</CardTitle>
        <p className="text-[11px] font-mono text-muted-foreground mt-0.5">{formula}</p>
        {assumptions && (
          <p className="text-[10px] text-muted-foreground/80 mt-0.5 leading-tight whitespace-pre-line">
            {assumptions}
          </p>
        )}
      </CardHeader>
      <CardContent className="pt-0 space-y-3">
        <div>
          <div className="text-[11px] uppercase tracking-wide text-muted-foreground">
            Fair Value
          </div>
          <div className="font-display text-3xl tabular-nums font-semibold leading-tight">
            {fairValue != null ? formatLKR(fairValue) : "—"}
          </div>
        </div>

        <div className="border-t border-border/60 pt-2 space-y-1">
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">Current Price</span>
            <span className="font-mono tabular-nums" data-testid={`${testId}-price`}>
              {currentPrice != null ? formatLKR(currentPrice) : "—"}
            </span>
          </div>
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">Difference</span>
            <span className={`font-mono tabular-nums font-medium ${diffColor}`} data-testid={`${testId}-delta`}>
              {delta != null ? fmtPct(delta, true) : "—"}
            </span>
          </div>
        </div>

        <p className="text-[11.5px] leading-snug text-muted-foreground">
          {interpretation}
        </p>
      </CardContent>
    </Card>
  );
};

const IntrinsicValueSection = ({ score }) => {
  const isTrust = !!score?.is_investment_trust;
  const price = score?.current_price ?? null;
  const grahamFv = score?.layer4_graham_number ?? null;
  const ddmFv = score?.layer4_ddm_fair_value ?? null;
  const grahamDelta = computeDeltaPct(price, grahamFv);
  const ddmDelta = computeDeltaPct(price, ddmFv);
  // hasDps: we infer from DDM presence (DDM requires DPS > 0)
  const hasDps = ddmFv != null && ddmFv > 0;
  const wrapperBorder = combinedBorder(grahamDelta, ddmDelta);

  return (
    <section className="space-y-3" data-testid="intrinsic-value-section">
      <div className="flex items-baseline justify-between flex-wrap gap-2">
        <div>
          <h3 className="font-display text-lg">Intrinsic Value Estimates</h3>
          <p className="text-xs text-muted-foreground">
            Calculated from earnings and dividend data.
          </p>
        </div>
      </div>

      {isTrust ? (
        // Single grey "not applicable" card for investment trusts
        <Card
          className="border-2 border-border bg-muted/40"
          data-testid="intrinsic-value-trust-card"
        >
          <CardContent className="p-5 space-y-1.5">
            <div className="font-display text-base">Valuation models not applicable</div>
            <p className="text-sm text-muted-foreground leading-snug">
              This is an investment holding company. Graham Number and DDM
              are not meaningful for investment trusts. Assess based on NAV
              discount and portfolio quality.
            </p>
          </CardContent>
        </Card>
      ) : (
        <>
          <div className={`rounded-lg p-1 ${wrapperBorder} border-0`}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <ValuationCard
                title="Graham Number"
                formula="√(22.5 × EPS × BVPS)"
                assumptions={null}
                fairValue={grahamFv}
                currentPrice={price}
                delta={grahamDelta}
                interpretation={interpretGraham(grahamDelta)}
                testId="graham-card"
              />
              <ValuationCard
                title="Dividend Discount Model"
                formula="DPS ÷ (r − g)"
                assumptions={"r = 12% required return\ng = 5% dividend growth"}
                fairValue={ddmFv}
                currentPrice={price}
                delta={ddmDelta}
                interpretation={interpretDdm(ddmDelta, hasDps)}
                testId="ddm-card"
              />
            </div>
          </div>

          {/* Caveat / context notice */}
          <div
            className="flex items-start gap-2 rounded-md border border-border/70 bg-muted/40 p-3 text-[11.5px] leading-snug text-muted-foreground"
            data-testid="intrinsic-value-caveat"
          >
            <Info className="h-3.5 w-3.5 mt-0.5 shrink-0" />
            <span>
              These are simplified valuation models that provide reference
              points, not precise targets.{" "}
              <strong className="text-foreground font-medium">Graham Number</strong>{" "}
              suits asset-heavy businesses.{" "}
              <strong className="text-foreground font-medium">DDM</strong>{" "}
              suits consistent dividend payers. Neither applies well to
              growth stocks or investment trusts.
            </span>
          </div>
        </>
      )}
    </section>
  );
};

export default IntrinsicValueSection;
